// File: app/src/components/workout/FreeTrainingChecklist.js
//
// Free training's checklist (#164): what you mean to do, jotted before you
// start, to tick off in any order.
//
// It's a plan like any other, edited with the plan editor every other
// session uses (SessionPlanDisplay + usePlanEditor, and the step dialog for
// a step's type, time / reps / calories, load, effort and notes). What's
// particular to it is the quick way in: type steps the way you type in the
// log ("bench 3x8 80", "pull-ups", "row 2k"), or start from your last
// session or a saved one, whose steps are copied as they are.

import React, { useMemo, useRef, useState } from "react";
import { Bookmark, Check, History, ListChecks, Plus, Star } from "lucide-react";
import { Button } from "../ui/button";
import DetailSheet from "../session/generate/DetailSheet";
import SessionPlanDisplay from "../plan/SessionPlanDisplay";
import usePlanEditor from "../../hooks/usePlanEditor";
import { AddRow, Group, ItemRow, Tag } from "../profile/ProfileParts";
import { cn } from "../../lib/utils";
import { kindStyle } from "./freeTrainingParts";
import {
  addToChecklist,
  checklistEmpty,
  checklistFigure,
  checklistFromSession,
  checklistFromText,
  checklistItem,
  checklistItems,
  checklistNode,
  defaultLast,
  describeLast,
  exerciseId,
  identifyExercise,
  parseChecklistText,
  splitBlockText,
  suggestExercises,
  typedName,
} from "../../utils/freeTrainingLog";

const plural = (n, one) => `${n} ${n === 1 ? one : `${one}s`}`;

const dayLabel = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })
    : "";
};

const lastFor = (item, list) =>
  list.find((e) => e.id === exerciseId(item.name))?.last || defaultLast(item.kind, item.name);

/** "As last time · 80 kg × 8", for an item with no numbers of its own. */
export const asLastTime = (item, list = []) => `As last time · ${describeLast(item.kind, lastFor(item, list))}`;

/** One checklist row: its kind's icon, name, and numbers. */
export const ChecklistRow = ({ item, detail, tag, onClick }) => (
  <ItemRow
    Icon={kindStyle(item.kind).icon}
    title={item.name}
    detail={detail}
    tag={tag ? <Tag>{tag}</Tag> : null}
    onClick={onClick}
  />
);

// The plan editor over the checklist. Inside a sheet, the rows' own drag
// (to reorder) mustn't drag the sheet shut.
const ChecklistEditor = ({ checklist, onChange }) => {
  const { planDisplayProps } = usePlanEditor(onChange);
  return (
    <div data-vaul-no-drag className="min-w-0">
      <SessionPlanDisplay plan={checklist} editable showTotal={false} {...planDisplayProps} />
    </div>
  );
};

/**
 * The checklist on Free training's start screen: "Add a checklist", or the
 * checklist in the plan editor with a row to add more.
 */
export const ChecklistGroup = ({ checklist, onChange, onOpen }) => {
  const empty = checklistEmpty(checklist);
  const count = empty ? 0 : checklistItems(checklist).length;
  if (empty) {
    return (
      <Group title="Checklist" note="Optional">
        <ItemRow
          Icon={ListChecks}
          title="Add a checklist"
          detail="Jot down what you'll do. Tick it off in any order."
          onClick={onOpen}
        />
      </Group>
    );
  }
  return (
    <section className="grid min-w-0 gap-2 text-left">
      <div className="flex items-baseline justify-between gap-2 px-0.5">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Checklist</h2>
        <p className="truncate text-xs text-muted-foreground/70">{plural(count, "exercise")}</p>
      </div>
      <ChecklistEditor checklist={checklist} onChange={onChange} />
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <AddRow label="Add to checklist" onClick={onOpen} />
      </div>
      <p className="px-0.5 text-xs leading-snug text-muted-foreground">
        Tick these off in any order once you start. Anything else you do still counts.
      </p>
    </section>
  );
};

/**
 * At Finish: checklist items that weren't logged, each with Add for one that
 * was done but not logged. `added` holds the ids added here.
 */
export const NotLoggedList = ({ rows, added = [], list = [], onAdd }) => (
  <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card text-left">
    {rows.map((r) => {
      const isAdded = added.includes(r.item.id);
      const Icon = kindStyle(r.item.kind).icon;
      const detail =
        !isAdded && r.done ? `${r.left} of ${r.planned} sets left` : checklistFigure(r.item) || asLastTime(r.item, list);
      return (
        <div key={r.item.id} className="grid grid-cols-[1.25rem_minmax(0,1fr)_auto] items-center gap-2.5 px-3 py-2.5">
          <Icon className="h-[18px] w-[18px] text-muted-foreground" aria-hidden="true" />
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium text-foreground">{r.item.name}</span>
            <span className="mt-0.5 block truncate text-xs text-muted-foreground">{detail}</span>
          </span>
          {isAdded ? (
            <span className="inline-flex h-8 items-center gap-1 text-[13px] font-semibold text-green-500">
              <Check className="h-4 w-4" aria-hidden="true" />
              Added
            </span>
          ) : (
            <Button type="button" variant="outline" size="sm" className="h-8 gap-1 px-2.5" onClick={() => onAdd(r)}>
              <Plus className="h-3.5 w-3.5" />
              Add
            </Button>
          )}
        </div>
      );
    })}
  </div>
);

/**
 * The quick way to add to the checklist: type, or start from a session. The
 * checklist itself is below it, in the plan editor.
 *
 * @param {object} props
 * @param {any} props.checklist     coachState.freeChecklist (a plan, or null)
 * @param {(change: (plan: any) => any) => void} props.onChange  a plan setter
 * @param {any[]} props.list        your exercise list (buildExerciseList)
 * @param {any[]} props.sessions    sessions to start from (checklistSessions)
 * @param {() => void} props.onClose
 * @param {string} [props.title]        the sheet's title and description, for
 * @param {string} [props.description]  a sheet that isn't Free training's (#173)
 * @param {React.ReactNode} [props.footer]  replaces Done, e.g. a Save button
 * @param {string} [props.addLabel]     the typing box's accessible name
 * @param {React.ReactNode} [props.extra]   shown below the list, e.g. Counts as
 */
export default function ChecklistSheet({
  checklist,
  onChange,
  list = [],
  sessions = [],
  onClose,
  title = "Checklist",
  description = "Jot down what you'll do. Tick it off in any order.",
  footer,
  addLabel = "Add to checklist",
  extra,
}) {
  const [view, setView] = useState("list");
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  const parsed = useMemo(() => parseChecklistText(query), [query]);
  const options = useMemo(() => suggestExercises(list, parsed.text), [list, parsed.text]);
  // What you typed is what's added, as typed; a suggestion's name only when
  // you pick it.
  const typed = typedName(query);
  const typedBlock = splitBlockText(typed);
  const known = (() => {
    const ident = identifyExercise(parsed.text);
    return !!ident.kind || list.some((e) => e.id === ident.id);
  })();
  const showTyped = !!typed && !options.some((e) => e.name.toLowerCase() === typed.toLowerCase());
  const typedNumbers =
    parsed.reps != null || parsed.sets != null || !!parsed.m || !!parsed.s || !!parsed.cal || !!parsed.rounds;

  const addSteps = (steps) => onChange((plan) => addToChecklist(plan, steps));
  const addItem = (item) => {
    if (item) addSteps(checklistNode(item, list));
    setQuery("");
    inputRef.current?.focus();
  };
  const close = (open) => !open && onClose();

  if (view === "saved") {
    // Favourites first, then the newest.
    const ordered = [...sessions].sort((a, b) => Number(b.favourite) - Number(a.favourite));
    return (
      <DetailSheet
        open
        onOpenChange={close}
        title="A saved session"
        description="Adds its exercises as they are: their numbers, types and notes. Not its rests."
        showDescription
        onBack={() => setView("list")}
      >
        <Group title="Your sessions">
          {ordered.map((s) => (
            <ItemRow
              key={s.id}
              Icon={s.favourite ? Star : Bookmark}
              title={s.name}
              detail={[dayLabel(s.at), s.names.join(", ")].filter(Boolean).join(" · ")}
              onClick={() => {
                addSteps(checklistFromSession({ children: s.steps }));
                setView("list");
              }}
            />
          ))}
        </Group>
      </DetailSheet>
    );
  }

  const [lastSession] = sessions;
  return (
    <DetailSheet
      open
      onOpenChange={close}
      title={title}
      description={description}
      showDescription
      footer={footer}
    >
      <div className="grid gap-2">
        <form
          className="grid grid-cols-[minmax(0,1fr)_48px] gap-2"
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            if (query.trim()) addItem(checklistFromText(query, list));
          }}
        >
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            enterKeyHint="done"
            placeholder="bench 3x8 80, pull-ups, row 2k"
            aria-label={addLabel}
            className="h-12 min-w-0 rounded-xl border border-border bg-card px-3.5 text-base outline-none placeholder:text-muted-foreground focus:border-primary/45 focus:ring-[3px] focus:ring-primary/15"
          />
          <Button type="submit" aria-label={addLabel} className="h-12 w-12 p-0 [&_svg]:size-6">
            <Plus />
          </Button>
        </form>
        {query.trim() && (options.length > 0 || showTyped) && (
          <div className="overflow-hidden rounded-xl border border-border bg-card" role="listbox" aria-label="Suggestions">
            <p className="px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
              {typedNumbers ? "Tap to add" : "Your exercises"}
            </p>
            {showTyped && (
              <button
                type="button"
                role="option"
                aria-selected
                onClick={() => addItem(checklistFromText(query, list))}
                className="grid w-full grid-cols-[18px_minmax(0,1fr)_auto] items-center gap-2.5 border-t border-border/60 bg-muted/50 px-3 py-2.5 text-left"
              >
                <Plus className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="truncate">{known || typedBlock ? `“${typed}”` : `Add “${typed}”`}</span>
                <span className="font-timer text-xs text-muted-foreground">
                  {checklistFigure(checklistFromText(query, list) || {}) || (known || typedBlock ? "" : "new")}
                </span>
              </button>
            )}
            {options.map((e) => {
              const style = kindStyle(e.kind);
              const Icon = style.icon;
              const item = checklistItem(e, parsed);
              return (
                <button
                  key={e.id}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => addItem(item)}
                  className="grid w-full grid-cols-[18px_minmax(0,1fr)_auto] items-center gap-2.5 border-t border-border/60 px-3 py-2.5 text-left"
                >
                  <Icon className={cn("h-4 w-4", style.text)} aria-hidden="true" />
                  <span className="truncate">{e.name}</span>
                  <span className="font-timer text-xs tabular-nums text-muted-foreground">
                    {(item && checklistFigure(item)) || describeLast(e.kind, e.last)}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {!checklistEmpty(checklist) && <ChecklistEditor checklist={checklist} onChange={onChange} />}

      {extra}

      {sessions.length > 0 && (
        <Group title="Start from">
          <ItemRow
            Icon={History}
            title="Last time's exercises"
            detail={[lastSession.name, dayLabel(lastSession.at)].filter(Boolean).join(" · ")}
            onClick={() => addSteps(checklistFromSession({ children: lastSession.steps }))}
          />
          {sessions.length > 1 && (
            <ItemRow
              Icon={Bookmark}
              title="A saved session"
              detail="Its exercises as they are"
              onClick={() => setView("saved")}
            />
          )}
        </Group>
      )}
    </DetailSheet>
  );
}
